import React, { useState, useMemo, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import forestIcon from '../assets/forest.png';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import CloseIcon from '@material-ui/icons/Close';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TablePagination from '@material-ui/core/TablePagination';
import Collapse from '@material-ui/core/Collapse';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import GetAppIcon from '@material-ui/icons/GetApp';
import Chip from '@material-ui/core/Chip';
import CheckIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Cancel';
import TextField from '@material-ui/core/TextField';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import CircularProgress from '@material-ui/core/CircularProgress';
import EmojiObjectsIcon from '@material-ui/icons/EmojiObjects';
import { useDeforestationExplanation } from '@graniot/common/adapters/parcels';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Card from '@material-ui/core/Card';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import * as XLSX from 'xlsx';
// eslint-disable-next-line
import html2pdf from 'html2pdf.js/dist/html2pdf.min';
import { marked } from 'marked';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import TracesWarning from './TracesWarning';
import { hasTracesWarning, tracesIssueCopy, tracesIssues } from '../../utils/traces';

const RISK_INDICATORS = [
  { field: 'Ind_01_treecover', label: 'Tree cover', number: '01', yesIsSafe: false },
  { field: 'Ind_02_commodities', label: 'Commodities', number: '02', yesIsSafe: true },
  { field: 'Ind_03_disturbance_before_2020', label: 'Disturbance before 2020', number: '03', yesIsSafe: true },
  { field: 'Ind_04_disturbance_after_2020', label: 'Disturbance after 2020', number: '04', yesIsSafe: false },
];

const CONTEXT_LAYER_FIELDS = [
  { field: 'ForTy_forest_2020', label: 'ForTy Forest 2020', hectares: true, group: 'land' },
  { field: 'ForTy_tree_crops_2020', label: 'ForTy Tree Crops 2020', hectares: true, group: 'land' },
  { field: 'ForTy_primary_2020', label: 'ForTy Primary 2020', hectares: true, group: 'land' },
  { field: 'ForTy_nat_reg_2020', label: 'ForTy Naturally Regenerating 2020', hectares: true, group: 'land' },
  { field: 'ForTy_planted_2020', label: 'ForTy Planted 2020', hectares: true, group: 'land' },
  { field: 'ForTy_plantation_2020', label: 'ForTy Plantation 2020', hectares: true, group: 'land' },
  {
    field: 'fields_of_the_world_overlap_percent',
    label: 'Fields of the World overlap',
    group: 'boundaries',
  },
  {
    field: 'overlaps_protected_areas',
    label: 'Protected areas',
    boolean: true,
    includeInExcel: false,
    group: 'boundaries',
  },
  {
    field: 'overlaps_indigenous_areas',
    label: 'Indigenous areas',
    boolean: true,
    includeInExcel: false,
    group: 'boundaries',
  },
];

const CONTEXT_LAYER_GROUPS = [
  { key: 'land', label: 'Land cover (2020)' },
  { key: 'boundaries', label: 'Boundary checks' },
];

const contextPercentage = (row, descriptor) => {
  const value = row?.[descriptor.field];
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  if (!descriptor.hectares) return numericValue;

  const unit = String(row?.Unit || '').trim().toLowerCase();
  if (['percent', 'percentage', '%'].includes(unit)) {
    return Math.max(0, Math.min(100, numericValue));
  }

  const area = Number(row?.Area);
  if (!Number.isFinite(area) || area <= 0) return null;
  return Math.max(0, Math.min(100, numericValue / area * 100));
};

const formatPercentage = (value) => {
  if (value === null || value === undefined) return '—';
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
};

const normalizeBoolean = value => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  const normalized = String(value || '').trim().toLowerCase();
  if (['true', 'yes', '1'].includes(normalized)) return true;
  if (['false', 'no', '0'].includes(normalized)) return false;
  return null;
};

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
      boxShadow: '0 4px 12px rgba(0,0,0,0.16)',
    },
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    border: '1px solid rgba(0,0,0,0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
    marginLeft: 8,
    marginBottom: 10,
    height: 42,
    width: 42,
    padding: 0,
    transition: 'all 0.2s ease',
  },
  // Public map: same height as the "Select parcel" box beside it (its 48px search
  // icon button; 44px with the small icons used at phone width).
  publicButton: {
    height: 48,
    width: 48,
    [theme.breakpoints.down('xs')]: {
      height: 44,
      width: 44,
    },
  },
  icon: {
    height: '28px',
    width: '28px',
    transition: 'transform 0.2s ease-in-out',
    '$button:hover &': {
      transform: 'scale(1.1)',
    },
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #232C57, #1a2145)',
    color: theme.palette.primary.contrastText,
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.contrastText,
    zIndex: 1,
    transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      transform: 'rotate(90deg)',
    },
  },
  noDataMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    minHeight: '200px',
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  summaryTableContainer: {
    backgroundColor: '#fff',
    borderRadius: '10px 10px 0 0',
    border: '1px solid rgba(0,0,0,0.06)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
    overflowX: 'auto',
    overscrollBehaviorX: 'contain',
  },
  summaryTable: {
    minWidth: 760,
    borderCollapse: 'separate',
    borderSpacing: 0,
    '& .MuiTableCell-root': {
      borderBottom: 'none',
    },
    '& .MuiTableCell-head': {
      backgroundColor: '#fff',
      borderBottom: '1px solid rgba(0,0,0,0.06)',
      color: '#858A98',
      fontFamily: '"Fira Sans", sans-serif',
      fontSize: '0.66rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      lineHeight: 1.25,
      padding: theme.spacing(1.25, 1.5),
    },
    '& .MuiTableCell-body': {
      fontSize: '0.82rem',
      color: '#3C4055',
      padding: theme.spacing(1, 1.5),
      borderBottom: '1px solid rgba(0,0,0,0.04)',
    },
  },
  expandCell: {
    width: 44,
    paddingLeft: `${theme.spacing(0.75)}px !important`,
    paddingRight: `${theme.spacing(0.5)}px !important`,
  },
  actionCell: {
    width: 88,
  },
  summaryParcelCell: {
    maxWidth: 340,
  },
  parcelNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
  },
  parcelName: {
    display: 'block',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 600,
    color: '#303448',
  },
  parcelId: {
    display: 'block',
    marginTop: 2,
    color: '#9A9EAA',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
  },
  summaryDateCell: {
    width: 190,
    color: '#777C8C !important',
    whiteSpace: 'nowrap',
  },
  summaryRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
    '& .MuiTableCell-body': {
      backgroundColor: '#fff',
      transition: 'background-color 0.15s ease',
    },
    '&:hover .MuiTableCell-body': {
      backgroundColor: 'rgba(92,107,63,0.04)',
    },
    '&:hover .MuiTableCell-body:first-child': {
      boxShadow: 'inset 3px 0 0 #5C6B3F',
    },
  },
  summaryRowExpanded: {
    '& .MuiTableCell-body': {
      backgroundColor: 'rgba(92,107,63,0.06)',
    },
    '& .MuiTableCell-body:first-child': {
      boxShadow: 'inset 3px 0 0 #5C6B3F',
    },
  },
  detailRow: {
    '& > td': {
      padding: 0,
      backgroundColor: '#FAF9F7',
      borderBottom: '1px solid rgba(0,0,0,0.05)',
    },
  },
  detailPanel: {
    padding: theme.spacing(2, 2.5, 2.5),
    borderTop: '1px solid rgba(0,0,0,0.05)',
  },
  detailHeading: {
    marginBottom: theme.spacing(1.25),
    fontFamily: '"Fira Sans", sans-serif',
    fontSize: '0.66rem',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: '#858A98',
    fontWeight: 600,
  },
  contextHeading: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2.25),
    marginBottom: theme.spacing(1.25),
  },
  contextGroups: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: theme.spacing(1.5),
  },
  contextGroupCard: {
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 9,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(35,44,87,0.04)',
  },
  contextGroupLand: {
    borderTop: `3px solid ${theme.palette.success.dark}`,
  },
  contextGroupBoundaries: {
    borderTop: `3px solid ${theme.palette.primary.main}`,
  },
  contextGroupHeader: {
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.primary,
    fontFamily: '"Fira Sans", sans-serif',
    fontSize: '0.76rem',
    fontWeight: 600,
  },
  indicatorGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    columnGap: theme.spacing(4),
  },
  indicatorRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    minHeight: 36,
    padding: theme.spacing(0.6, 0),
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    fontSize: '0.8rem',
  },
  indicatorLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
    color: '#3C4055',
  },
  indicatorNumber: {
    flexShrink: 0,
    padding: '2px 6px',
    borderRadius: 4,
    border: '1px solid rgba(0,0,0,0.06)',
    backgroundColor: '#fff',
    color: '#858A98',
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.65rem',
    fontWeight: 600,
  },
  contextValue: {
    flexShrink: 0,
    minWidth: 58,
    textAlign: 'right',
    color: '#303448',
    fontFamily: '"Roboto Mono", monospace',
    fontWeight: 600,
  },
  contextStatus: {
    display: 'inline-flex',
    alignItems: 'center',
    minWidth: 78,
    justifyContent: 'center',
    padding: '3px 8px',
    borderRadius: 999,
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.66rem',
    fontWeight: 600,
  },
  contextOverlap: {
    color: theme.palette.error.dark,
    backgroundColor: 'rgba(198,40,40,0.08)',
    border: '1px solid rgba(198,40,40,0.18)',
  },
  contextClear: {
    color: theme.palette.success.dark,
    backgroundColor: 'rgba(46,125,50,0.08)',
    border: '1px solid rgba(46,125,50,0.18)',
  },
  contextUnknown: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.action.hover,
    border: `1px dashed ${theme.palette.divider}`,
  },
  indicatorChip: {
    height: 23,
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  indicatorSafe: {
    backgroundColor: 'rgba(46,125,50,0.14)',
    color: '#1B5E20',
    border: '1px solid rgba(46,125,50,0.26)',
  },
  indicatorConcern: {
    backgroundColor: 'rgba(198,40,40,0.09)',
    color: '#C62828',
    border: '1px solid rgba(198,40,40,0.18)',
  },
  indicatorEmpty: {
    backgroundColor: 'transparent',
    color: '#A0A4AE',
    border: '1px dashed rgba(0,0,0,0.12)',
  },
  riskBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 10px',
    borderRadius: 7,
    fontSize: '0.68rem',
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    '&::before': {
      content: '""',
      width: 6,
      height: 6,
      borderRadius: 999,
      backgroundColor: 'currentColor',
    },
  },
  riskHigh: {
    backgroundColor: 'rgba(255,0,0,0.08)',
    color: '#D7191C',
    boxShadow: '0 0 0 1px rgba(215,25,28,0.14)',
  },
  riskLow: {
    backgroundColor: 'rgba(0,102,255,0.08)',
    color: '#0066FF',
    boxShadow: '0 0 0 1px rgba(0,102,255,0.14)',
  },
  riskMore: {
    backgroundColor: 'rgba(30,187,255,0.10)',
    color: '#0498D0',
    boxShadow: '0 0 0 1px rgba(30,187,255,0.18)',
  },
  riskUnknown: {
    backgroundColor: 'rgba(0,0,0,0.05)',
    color: '#777C8C',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
  },
  chevronButton: {
    width: 28,
    height: 28,
    color: '#8A8E9A',
    transition: 'transform 0.2s ease, background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(92,107,63,0.08)',
      color: '#5C6B3F',
    },
  },
  chevronButtonOpen: {
    transform: 'rotate(90deg)',
    color: '#5C6B3F',
  },
  summaryPagination: {
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.06)',
    borderTop: 'none',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    color: '#777C8C',
    '& .MuiTablePagination-toolbar': {
      minHeight: 44,
    },
  },
  explanationFullWidth: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  downloadButton: {
    background: 'linear-gradient(135deg, #232C57, #1a2145)',
    borderRadius: 8,
    height: 36,
    fontSize: '0.8rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 2px 6px rgba(35,44,87,0.25)',
    '&:hover': {
      background: 'linear-gradient(135deg, #1a2145, #141a38)',
      boxShadow: '0 2px 8px rgba(35,44,87,0.32)',
    },
    color: theme.palette.primary.contrastText,
    whiteSpace: 'nowrap',
    transition: 'background 0.2s ease, box-shadow 0.2s ease',
  },
  pdfDownloadButton: {
    backgroundColor: theme.palette.error.main,
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
      transform: 'translateY(-1px)',
      boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    },
    color: theme.palette.error.contrastText,
    height: 32,
    whiteSpace: 'nowrap',
    transition: 'all 0.2s ease-in-out',
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  dialogContent: {
    padding: theme.spacing(3),
    background: 'linear-gradient(180deg, #FAF9F7 0%, #F5F3F0 100%)',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  dataGridSide: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'auto',
    maxHeight: '100%',
    overscrollBehavior: 'contain',
    transition: 'all 0.3s ease-in-out',
    [theme.breakpoints.down('md')]: {
      display: props => props.showExplanation ? 'none' : 'block',
      flex: props => props.showExplanation ? '0 0 0%' : '1 1 auto',
    },
  },
  controlsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    borderBottom: '1px solid rgba(0,0,0,0.06)',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  controlsRight: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
  },
  searchField: {
    minWidth: 250,
    maxWidth: 400,
    margin: theme.spacing(0.5, 0),
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
      backgroundColor: '#fff',
      fontSize: '0.82rem',
      transition: 'border-color 0.15s ease',
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#5C6B3F',
        },
      },
    },
  },
  searchIcon: {
    color: theme.palette.primary.main,
  },
  markdownContent: {
    whiteSpace: 'normal',
    lineHeight: 1.5,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      margin: '16px 0 10px 0',
      color: theme.palette.primary.main,
      lineHeight: 1.3,
      fontWeight: 600,
    },
    '& h1': {
      fontSize: '1.5rem',
      borderBottom: `1px solid ${theme.palette.divider}`,
      paddingBottom: '8px',
    },
    '& h2': {
      fontSize: '1.3rem',
    },
    '& h3': {
      fontSize: '1.1rem',
      display: 'flex',
      alignItems: 'center',
      '&::before': {
        content: '""',
        display: 'inline-block',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: theme.palette.primary.main,
        marginRight: '8px',
      }
    },
    '& strong, & b': {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    '& em, & i': {
      fontStyle: 'italic',
    },
    '& p': {
      margin: '8px 0',
    },
    '& ul, & ol': {
      paddingLeft: 24,
      margin: '8px 0',
    },
    '& li': {
      margin: '4px 0',
      position: 'relative',
    },
    '& hr': {
      margin: '16px 0',
      border: 'none',
      height: '1px',
      backgroundColor: theme.palette.divider,
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.light}`,
      padding: '0 16px',
      margin: '16px 0',
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      margin: '16px 0',
      boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
    },
    '& th, & td': {
      padding: '10px 12px',
      border: `1px solid ${theme.palette.divider}`,
      textAlign: 'left',
    },
    '& th': {
      backgroundColor: theme.palette.action.hover,
      fontWeight: 'bold',
      color: theme.palette.primary.dark,
    },
    '& tr:nth-child(even)': {
      backgroundColor: 'rgba(0, 0, 0, 0.02)',
    },
    '& code': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
      padding: '3px 6px',
      borderRadius: 4,
      fontFamily: '"Roboto Mono", monospace',
      fontSize: '0.85em',
      color: theme.palette.primary.dark,
      border: `1px solid ${theme.palette.divider}`,
    },
    '& a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      }
    },
    '& img': {
      maxWidth: '100%',
      borderRadius: 4,
      margin: '8px 0',
    },
    '& ul li:has(input[type="checkbox"]:checked)::before': {
      content: '"✓"',
      color: theme.palette.success.main,
      fontWeight: 'bold',
      position: 'absolute',
      left: '-20px',
    },
    '& ul li:has(input[type="checkbox"]:not(:checked))::before': {
      content: '"✗"',
      color: theme.palette.error.main,
      fontWeight: 'bold',
      position: 'absolute',
      left: '-20px',
    },
  },
  statsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginLeft: 0,
    [theme.breakpoints.up('md')]: {
      marginLeft: theme.spacing(2),
    },
    flexWrap: 'wrap',
  },
  statChip: {
    fontWeight: 600,
    fontSize: '0.68rem',
    height: 25,
    borderRadius: 7,
    boxShadow: 'none',
    margin: 0,
  },
  lowRiskChip: {
    backgroundColor: 'rgba(0,102,255,0.08)',
    color: '#0066FF',
  },
  highRiskChip: {
    backgroundColor: 'rgba(255,0,0,0.08)',
    color: '#D7191C',
  },
  moreInfoChip: {
    backgroundColor: 'rgba(30,187,255,0.10)',
    color: '#0498D0',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    backgroundColor: theme.palette.background.paper,
    flexDirection: 'column',
    minHeight: '200px',
  },
  actionButton: {
    width: 30,
    height: 30,
    color: '#777C8C',
    backgroundColor: '#fff',
    border: '1px solid rgba(0,0,0,0.06)',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(35,44,87,0.06)',
      color: '#232C57',
    },
  },
  lightbulbIcon: {
    color: theme.palette.text.secondary,
    transition: 'all 0.3s ease-in-out',
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
  dialogPaper: {
    maxWidth: '95vw',
    minHeight: '75vh',
    maxHeight: '85vh',
    borderRadius: 12,
    transition: 'transform 0.3s ease-in-out',
    transform: 'translateY(0)',
    animation: '$dialogFadeIn 0.4s ease-out',
    overflow: 'hidden',
    boxShadow: '0 12px 28px rgba(0,0,0,0.18)',
  },
  '@keyframes dialogFadeIn': {
    '0%': {
      opacity: 0.8,
      transform: 'translateY(20px) scale(0.98)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  loadingAnimation: {
    animation: '$pulse 1.5s infinite ease-in-out',
  },
  '@keyframes pulse': {
    '0%': {
      opacity: 0.6,
    },
    '50%': {
      opacity: 1,
    },
    '100%': {
      opacity: 0.6,
    },
  },
  explanationHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: 'linear-gradient(180deg, #FAF9F7 0%, #F5F3F0 100%)',
    flexShrink: 0,
  },
  explanationHeaderMain: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minWidth: 0,
  },
  backToResultsButton: {
    minHeight: 44,
    marginLeft: theme.spacing(-1),
    padding: theme.spacing(0.5, 1),
    borderRadius: 7,
    color: theme.palette.primary.main,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'background-color 160ms ease, color 160ms ease',
    '&:hover': {
      backgroundColor: 'rgba(35,44,87,0.07)',
      boxShadow: 'none',
    },
  },
  explanationTitle: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '1.1rem',
  },
  explanationContent: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(3),
  },
  selectedEvidence: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 6px 18px rgba(35,44,87,0.08)',
    animation: '$evidenceMorph 320ms cubic-bezier(0.2, 0.75, 0.25, 1) both',
  },
  selectedEvidenceSummary: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) minmax(150px, auto) minmax(100px, auto)',
    alignItems: 'center',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1),
    },
  },
  selectedParcelName: {
    display: 'block',
    color: theme.palette.text.primary,
    fontFamily: '"Fira Sans", sans-serif',
    fontSize: '1rem',
    fontWeight: 600,
  },
  selectedAuditDate: {
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
    whiteSpace: 'nowrap',
  },
  selectedEvidenceDetails: {
    paddingTop: theme.spacing(1.5),
  },
  explanationBox: {
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderTop: `3px solid ${theme.palette.primary.main}`,
    borderRadius: 10,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 6px 18px rgba(35,44,87,0.08)',
    animation: '$explanationRise 360ms 90ms cubic-bezier(0.2, 0.75, 0.25, 1) both',
  },
  explanationBoxHeader: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.primary,
    fontFamily: '"Fira Sans", sans-serif',
    fontSize: '0.86rem',
    fontWeight: 600,
  },
  explanationBoxBody: {
    padding: theme.spacing(2),
  },
  '@keyframes evidenceMorph': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-12px) scale(0.985)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  '@keyframes explanationRise': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    selectedEvidence: {
      animation: 'none',
    },
    explanationBox: {
      animation: 'none',
    },
    loadingAnimation: {
      animation: 'none',
    },
  },
}));

const DeforestationButton = ({ selectedFarm, isPublicMode=false }) => {
  const classes = useStyles({
    isDemLegendVisible: false,
    openDrawer: false,
    drawerWidth: 0,
    width: 0,
    showExplanation: false,
  });
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  const [searchTerm, setSearchTerm] = useState('');
  const [expandedParcelId, setExpandedParcelId] = useState(null);
  const [expandedIndicatorsId, setExpandedIndicatorsId] = useState(null);
  const [tablePage, setTablePage] = useState(0);
  const [explanationText, setExplanationText] = useState('');
  const [isExplanationLoading, setIsExplanationLoading] = useState(false);
  const explanationHeadingRef = useRef(null);
  const returnFocusParcelIdRef = useRef(null);

  // Auto-detect public mode if not explicitly passed
  const detectedPublicMode = isPublicMode ?? !!localStorage.getItem('graniot_public_api_key');

  // Function to format the assessment date into a more readable format
  const formatAssessmentDate = (dateStr) => {
    if (!dateStr) return '';

    try {
      return format(parseISO(dateStr), "dd MMMM yyyy HH:mm:ss");
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateStr;
    }
  };

  // Check if farm/data has parcels/geoms with deforestation assessment data
  const hasDeforestationData = useMemo(() => {
    if (!selectedFarm) return false;

    if (detectedPublicMode) {
      // For public mode, check direct features (geoms)
      const features = selectedFarm.features || [];
      if (features.length === 0) return false;

      // Check if any features have deforestation assessment data
      return features.some(feature => {
        const properties = feature.properties;
        return properties && properties.deforestation_audit;
      });
    } else {
      // For authenticated mode, use existing parcel logic
      if (!selectedFarm.farm_parcels) return false;

      // Get parcels from the GeoJSON features
      const parcels = selectedFarm.farm_parcels.features || [];
      if (parcels.length === 0) return false;

      // Check if any parcels have deforestation assessment data
      return parcels.some(feature => {
        const parcel = feature.properties;
        return parcel && parcel.deforestation_audit;
      });
    }
  }, [selectedFarm, detectedPublicMode]);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
    setExpandedIndicatorsId(null);
  };

  // Extract rows from all parcels/geoms with their deforestation data only
  const rows = useMemo(() => {
    if (!selectedFarm) return [];

    const rowData = [];

    // Get features based on mode
    const features = detectedPublicMode
      ? (selectedFarm.features || [])
      : (selectedFarm.farm_parcels?.features || []);

    // Process all features with their deforestation audit data
    features.forEach((feature, index) => {
      const properties = feature.properties;
      if (!properties) return;

      // Generate a unique ID for the row
      const itemId = feature.id || `unknown-item-${index}`;

      // Start with basic info - use generic names for both modes
      const rowItem = {
        id: itemId,
        parcel_id: itemId,  // Keep field name for consistency but it works for both
        parcel_name: properties.name || 'Unknown',
      };

      // Handle deforestation_audit as an array and get the most recent audit
      if (properties.deforestation_audit) {
        let mostRecentAudit;

        if (Array.isArray(properties.deforestation_audit)) {
          // Sort audits by date (most recent first)
          const sortedAudits = [...properties.deforestation_audit].sort((a, b) => {
            return new Date(b.date) - new Date(a.date);
          });

          // Get the most recent audit
          mostRecentAudit = sortedAudits.length > 0 ? sortedAudits[0] : null;
        } else {
          // Handle legacy format (single audit object)
          mostRecentAudit = properties.deforestation_audit;
        }

        // Add audit date to the row
        if (mostRecentAudit && mostRecentAudit.date) {
          rowItem.audit_date = formatAssessmentDate(mostRecentAudit.date);
          rowItem.audit_version = mostRecentAudit.date;
        }

        // Add audit data to the row
        if (mostRecentAudit && mostRecentAudit.data) {
          Object.entries(mostRecentAudit.data).forEach(([dataKey, dataValue]) => {
            rowItem[dataKey] = dataValue;
          });
        }
      }

      const computedAttributes = properties.computed_attributes || {};
      [
        'fields_of_the_world_overlap_percent',
        'overlaps_protected_areas',
        'overlaps_indigenous_areas',
      ].forEach(field => {
        if (
          rowItem[field] === undefined &&
          computedAttributes[field] !== undefined
        ) {
          rowItem[field] = computedAttributes[field];
        }
      });
      // TRACES verdict (company 400 only): the row is shaped like computed_attributes for TracesWarning.
      if (computedAttributes.traces) {
        rowItem.traces = computedAttributes.traces;
      }
      // Add the row with deforestation data
      rowData.push(rowItem);
    });

    return rowData;
  }, [selectedFarm, detectedPublicMode]);

  // Plain-language TRACES reasons for one row (empty unless the export would be rejected).
  const tracesReasons = (row) => (hasTracesWarning(row) ? tracesIssues(row) : [])
    .map(issue => {
      const { title, hint } = tracesIssueCopy(issue.code);
      return hint ? `${title}: ${hint}` : title;
    })
    .join('; ');

  // Download data as Excel
  const downloadExcel = () => {
    if (!rows || rows.length === 0) return;

    const columns = [
      { field: 'parcel_id', header: detectedPublicMode ? 'ID' : 'Parcel ID' },
      { field: 'parcel_name', header: detectedPublicMode ? 'Name' : 'Parcel Name' },
      { field: 'audit_date', header: 'Audit Date' },
      ...RISK_INDICATORS.map(indicator => ({
        field: indicator.field,
        header: `Ind ${indicator.number} ${indicator.label}`,
      })),
      { field: 'risk_acrop', header: 'Risk Acrop' },
      ...CONTEXT_LAYER_FIELDS
        .filter(descriptor => descriptor.includeInExcel !== false)
        .map(descriptor => ({
          field: descriptor.field,
          header: descriptor.excelHeader || (
            descriptor.field === 'fields_of_the_world_overlap_percent'
              ? 'Fields of the World Overlap (%)'
              : `${descriptor.label} (%)`
          ),
          context: descriptor,
        })),
      // TRACES flags, appended last and only for farms that carry the check (company 400),
      // so every other export keeps its columns.
      ...(rows.some(row => row.traces) ? [
        { field: 'traces_geometry', header: 'TRACES Geometry', traces: row => (hasTracesWarning(row) ? 'Not valid' : '') },
        { field: 'traces_issues', header: 'TRACES Issues', traces: tracesReasons },
      ] : []),
    ];

    const wsData = [
      columns.map(column => column.header),
      ...rows.map(row => columns.map(column => {
        if (column.traces) {
          return column.traces(row);
        }
        if (column.context?.boolean) {
          const value = normalizeBoolean(row[column.field]);
          return value === null ? '' : (value ? 'Yes' : 'No');
        }
        if (column.context) {
          const value = contextPercentage(row, column.context);
          return value === null ? '' : Math.round(value * 10) / 10;
        }
        const value = row[column.field];
        return value === null || value === undefined ? '' : value;
      })),
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData) || {};

    // Auto-size columns
    const colWidths = [];
    wsData[0].forEach((header, idx) => {
      let maxWidth = header.length;
      wsData.forEach(row => {
        if (row[idx]) {
          const cellLength = String(row[idx]).length;
          if (cellLength > maxWidth) {
            maxWidth = cellLength;
          }
        }
      });
      colWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    });
    ws['!cols'] = colWidths;

    // Style the header row
    if (ws['!ref']) {
      const headerRange = XLSX.utils.decode_range(ws['!ref']);
      for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!ws[cellAddress]) continue;
        ws[cellAddress].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E8E4F3" } },
          alignment: { horizontal: "center", vertical: "center" }
        };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Deforestation Assessment');

    // Generate Excel file
    const filename = `deforestation_assessment_${selectedFarm?.id}_${new Date().toLocaleDateString('en-GB').split('/').join('-')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  // Handle search term change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setTablePage(0);
  };

  // Download AI explanation as PDF
  const downloadExplanationPDF = async () => {
    if (!explanationText || !expandedParcelId) return;

    try {
      // Find the parcel name from the rows data
      const parcelData = rows.find(row => row.parcel_id === expandedParcelId);
      const parcelName = parcelData?.parcel_name || 'Unknown';

      // Configure marked options for better rendering
      marked.setOptions({
        breaks: true,
        gfm: true,
        headerIds: false,
        mangle: false
      });

      // Convert markdown to HTML
      const htmlContent = marked(explanationText);

      // Create a container element with the HTML content and styling
      const element = document.createElement('div');
      element.innerHTML = `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; color: #333;">
          <div style="border-bottom: 3px solid #1976d2; padding-bottom: 20px; margin-bottom: 30px;">
            <h1 style="color: #1976d2; margin: 0 0 10px 0; font-size: 32px;">Deforestation Risk Assessment</h1>
            <h2 style="color: #666; margin: 0 0 10px 0; font-size: 20px; font-weight: normal;">Parcel: ${parcelName} (ID: ${expandedParcelId})</h2>
            <p style="color: #999; margin: 0; font-size: 14px;">Generated on ${new Date().toLocaleDateString('en-GB')} at ${new Date().toLocaleTimeString('en-GB')}</p>
          </div>
          
          <div class="markdown-content" style="line-height: 1.8; font-size: 16px;">
            ${htmlContent}
          </div>
          
        </div>
      `;

      // Apply additional styling to the converted markdown content
      const markdownContainer = element.querySelector('.markdown-content');
      if (markdownContainer) {
        // Style headers
        const headers = markdownContainer.querySelectorAll('h1, h2, h3, h4, h5, h6');
        headers.forEach(header => {
          header.style.color = '#2c3e50';
          header.style.marginTop = '24px';
          header.style.marginBottom = '16px';
          header.style.fontWeight = '600';
        });

        // Style paragraphs
        const paragraphs = markdownContainer.querySelectorAll('p');
        paragraphs.forEach(p => {
          p.style.marginBottom = '16px';
          p.style.lineHeight = '1.7';
        });

        // Style lists
        const lists = markdownContainer.querySelectorAll('ul, ol');
        lists.forEach(list => {
          list.style.marginBottom = '16px';
          list.style.paddingLeft = '30px';
        });

        const listItems = markdownContainer.querySelectorAll('li');
        listItems.forEach(li => {
          li.style.marginBottom = '8px';
          li.style.lineHeight = '1.7';
        });

        // Style code blocks
        const codeBlocks = markdownContainer.querySelectorAll('code');
        codeBlocks.forEach(code => {
          code.style.backgroundColor = '#f4f4f4';
          code.style.padding = '2px 6px';
          code.style.borderRadius = '3px';
          code.style.fontFamily = 'Consolas, Monaco, monospace';
          code.style.fontSize = '14px';
        });

        // Style pre blocks
        const preBlocks = markdownContainer.querySelectorAll('pre');
        preBlocks.forEach(pre => {
          pre.style.backgroundColor = '#f8f8f8';
          pre.style.padding = '16px';
          pre.style.borderRadius = '6px';
          pre.style.overflow = 'auto';
          pre.style.marginBottom = '16px';
        });

        // Style strong/bold
        const strongs = markdownContainer.querySelectorAll('strong, b');
        strongs.forEach(strong => {
          strong.style.fontWeight = '600';
          strong.style.color = '#2c3e50';
        });

        // Style horizontal rules
        const hrs = markdownContainer.querySelectorAll('hr');
        hrs.forEach(hr => {
          hr.style.border = 'none';
          hr.style.borderTop = '1px solid #e0e0e0';
          hr.style.margin = '24px 0';
        });

        // Style tables if any
        const tables = markdownContainer.querySelectorAll('table');
        tables.forEach(table => {
          table.style.width = '100%';
          table.style.borderCollapse = 'collapse';
          table.style.marginBottom = '16px';
        });

        const ths = markdownContainer.querySelectorAll('th');
        ths.forEach(th => {
          th.style.backgroundColor = '#f5f5f5';
          th.style.padding = '12px';
          th.style.textAlign = 'left';
          th.style.borderBottom = '2px solid #ddd';
          th.style.fontWeight = '600';
        });

        const tds = markdownContainer.querySelectorAll('td');
        tds.forEach(td => {
          td.style.padding = '12px';
          td.style.borderBottom = '1px solid #eee';
        });
      }

      // Configure html2pdf options
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `deforestation_risk_assessment_parcel_${expandedParcelId}_${new Date().toLocaleDateString('en-GB').split('/').join('-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          letterRendering: true,
          useCORS: true,
          logging: false
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait' 
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      // Generate and save the PDF
      await html2pdf().set(opt).from(element).save();

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  // Filter rows based on search term
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;

    const normalizedSearchTerm = searchTerm.toLowerCase().trim();

    return rows.filter(row => {
      // Search in parcel_id
      if (row.parcel_id && row.parcel_id.toString().toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }

      // Search in parcel_name
      if (row.parcel_name && row.parcel_name.toString().toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }

      // Search in risk fields
      if (row.risk_acrop && row.risk_acrop.toString().toLowerCase().includes(normalizedSearchTerm)) {
        return true;
      }

      return false;
    });
  }, [rows, searchTerm]);

  const paginatedRows = useMemo(() => {
    const firstRow = tablePage * pageSize;
    return filteredRows.slice(firstRow, firstRow + pageSize);
  }, [filteredRows, tablePage, pageSize]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredRows.length / pageSize) - 1);
    if (tablePage > lastPage) setTablePage(lastPage);
  }, [filteredRows.length, pageSize, tablePage]);

  const riskPresentation = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized.includes('high')) return { label: 'HIGH', className: classes.riskHigh };
    if (normalized.includes('low')) return { label: 'LOW', className: classes.riskLow };
    if (normalized.includes('more_info') || normalized.includes('moreinfo')) {
      return { label: 'MORE INFO', className: classes.riskMore };
    }
    return { label: '—', className: classes.riskUnknown };
  };

  const renderIndicatorChip = (value, indicator) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'yes' || normalized === 'no') {
      const isYes = normalized === 'yes';
      const isSafe = isYes === indicator.yesIsSafe;
      return (
        <Chip
          size="small"
          label={isYes ? 'YES' : 'NO'}
          icon={isSafe ? <CheckIcon /> : <CancelIcon />}
          className={`${classes.indicatorChip} ${isSafe ? classes.indicatorSafe : classes.indicatorConcern}`}
          data-tone={isSafe ? 'safe' : 'concern'}
        />
      );
    }
    return (
      <Chip
        size="small"
        label={value || '—'}
        className={`${classes.indicatorChip} ${classes.indicatorEmpty}`}
      />
    );
  };

  const renderContextValue = (row, descriptor) => {
    if (!descriptor.boolean) {
      return (
        <span className={classes.contextValue}>
          {formatPercentage(contextPercentage(row, descriptor))}
        </span>
      );
    }

    const value = normalizeBoolean(row[descriptor.field]);
    if (value === null) {
      return (
        <span className={`${classes.contextStatus} ${classes.contextUnknown}`}>
          {t('Not calculated')}
        </span>
      );
    }
    return (
      <span className={`${classes.contextStatus} ${value ? classes.contextOverlap : classes.contextClear}`}>
        {value ? t('Overlap') : t('No overlap')}
      </span>
    );
  };

  // Calculate statistics - move this before any returns or conditionals
  const riskStats = useMemo(() => {
    if (!filteredRows || filteredRows.length === 0) return { low: 0, high: 0, info: 0, total: 0 };

    let low = 0;
    let high = 0;
    let info = 0;

    filteredRows.forEach(row => {
      // Check for the risk_acrop field
      const riskValue = row.risk_acrop ? String(row.risk_acrop).toLowerCase() : '';

      if (riskValue.includes('low')) {
        low++;
      } else if (riskValue.includes('high')) {
        high++;
      } else if (riskValue.includes('more_info') || riskValue.includes('moreinfo')) {
        info++;
      }
    });

    return {
      low,
      high,
      info,
      total: filteredRows.length
    };
  }, [filteredRows]);

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setTablePage(0);
    try {
      localStorage.setItem('deforestation-table-page-size', newPageSize.toString());
    } catch (e) {
      console.error('Error saving page size to localStorage:', e);
    }
  };

  // Load saved page size from localStorage on initial render
  useEffect(() => {
    try {
      const savedPageSize = localStorage.getItem('deforestation-table-page-size');
      if (savedPageSize) {
        const parsedSize = parseInt(savedPageSize, 10);
        if (!isNaN(parsedSize) && parsedSize > 0) {
          setPageSize(parsedSize);
        }
      }
    } catch (e) {
      console.error('Error loading page size from localStorage:', e);
    }
  }, []);

  // Open the explanation as a dedicated full-width view.
  const handleExplainClick = (parcelId) => {
    // Don't allow in public mode
    if (detectedPublicMode) return;

    setExpandedParcelId(parcelId);
    setExplanationText('');
    setIsExplanationLoading(true);
  };

  // Return to the parcel list without keeping the narrow split layout alive.
  const handleBackToTable = () => {
    returnFocusParcelIdRef.current = expandedParcelId;
    setExpandedParcelId(null);
    setExplanationText('');
    setIsExplanationLoading(false);
  };

  const selectedExplanationRow = useMemo(
    () => rows.find(row => row.parcel_id === expandedParcelId) || null,
    [expandedParcelId, rows],
  );
  const expandedAuditVersion = selectedExplanationRow?.audit_version;
  const {
    data: explanationData,
    isLoading: isExplanationQueryLoading,
    isError: isExplanationQueryError,
    error: explanationError,
  } = useDeforestationExplanation(
    detectedPublicMode ? null : expandedParcelId,
    expandedAuditVersion,
  );

  useEffect(() => {
    if (expandedParcelId) {
      explanationHeadingRef.current?.focus();
    }
  }, [expandedParcelId]);

  useEffect(() => {
    if (!expandedParcelId) return;

    if (explanationData?.status === 'success' && explanationData.explanation) {
      let processedText = explanationData.explanation;
      processedText = processedText.replace(/\n{3,}/g, '\n\n');
      processedText = processedText.replace(/\n---\n/g, '\n\n---\n\n');
      setExplanationText(processedText);
      setIsExplanationLoading(false);
      return;
    }

    if (isExplanationQueryError) {
      console.error('Error fetching explanation:', explanationError);
      setExplanationText('An error occurred while fetching the explanation.');
      setIsExplanationLoading(false);
      return;
    }

    setIsExplanationLoading(isExplanationQueryLoading);
  }, [
    expandedParcelId,
    explanationData,
    explanationError,
    isExplanationQueryError,
    isExplanationQueryLoading,
  ]);

  // Only render the button if we have deforestation data
  if (!hasDeforestationData) return null;

  return (
    <>
      <Tooltip title="Deforestation Assessment">
        <IconButton
          className={detectedPublicMode ? `${classes.button} ${classes.publicButton}` : classes.button}
          onClick={handleClick}
          aria-label="Deforestation Assessment"
        >
          <img src={forestIcon} alt="Forest" className={classes.icon} />
        </IconButton>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xl"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 300,
          },
          onEntering: () => {
            // Add any logic for entering animation if needed
          },
          onExiting: () => {
            // Reset explanation panel when dialog closes
            setExpandedParcelId(null);
            setExplanationText('');
          },
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          Deforestation Assessment
          <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {expandedParcelId && !detectedPublicMode ? (
            <div className={classes.explanationFullWidth} data-testid="deforestation-explanation">
              <div className={classes.explanationHeader}>
                  <div className={classes.explanationHeaderMain}>
                    <Button
                      variant="text"
                      color="primary"
                      size="medium"
                      className={classes.backToResultsButton}
                      onClick={handleBackToTable}
                      startIcon={<ArrowBackIcon />}
                      aria-label="Back to deforestation results"
                    >
                      {t('Deforestation results')}
                    </Button>
                    <Typography
                      variant="h6"
                      ref={explanationHeadingRef}
                      tabIndex={-1}
                      className={classes.explanationTitle}
                      data-testid="deforestation-explanation-title"
                    >
                      AI explanation · {selectedExplanationRow?.parcel_name || 'Unknown'} · ID {expandedParcelId}
                    </Typography>
                  </div>
                  {!isExplanationLoading && explanationText && (
                    <Button
                      variant="outlined"
                      size="small"
                      className={classes.pdfDownloadButton}
                      onClick={downloadExplanationPDF}
                      startIcon={<PictureAsPdfIcon />}
                    >
                      {t('Export PDF')}
                    </Button>
                  )}
              </div>
              <div className={classes.explanationContent}>
                  {selectedExplanationRow && (
                    <Card
                      className={classes.selectedEvidence}
                      data-testid="selected-explanation-row"
                      elevation={0}
                    >
                    <div className={classes.selectedEvidenceSummary}>
                      <div>
                        <span className={classes.selectedParcelName}>
                          {selectedExplanationRow.parcel_name}
                        </span>
                        <span className={classes.parcelId}>
                          ID {selectedExplanationRow.parcel_id}
                        </span>
                      </div>
                      <span className={classes.selectedAuditDate}>
                        {selectedExplanationRow.audit_date || '—'}
                      </span>
                      <span
                        className={`${classes.riskBadge} ${riskPresentation(selectedExplanationRow.risk_acrop).className}`}
                      >
                        {riskPresentation(selectedExplanationRow.risk_acrop).label}
                      </span>
                    </div>

                    <div className={classes.selectedEvidenceDetails}>
                      <div className={classes.detailHeading}>{t('Risk indicators')}</div>
                      <div className={classes.indicatorGrid}>
                        {RISK_INDICATORS.map(indicator => (
                          <div key={indicator.field} className={classes.indicatorRow}>
                            <span className={classes.indicatorLabel}>
                              <span className={classes.indicatorNumber}>I{indicator.number}</span>
                              {t(indicator.label)}
                            </span>
                            {renderIndicatorChip(selectedExplanationRow[indicator.field], indicator)}
                          </div>
                        ))}
                      </div>

                      <div className={`${classes.detailHeading} ${classes.contextHeading}`}>
                        {t('Context layers')}
                      </div>
                      <div className={classes.contextGroups}>
                        {CONTEXT_LAYER_GROUPS.map(group => (
                          <section
                            key={group.key}
                            className={`${classes.contextGroupCard} ${group.key === 'land' ? classes.contextGroupLand : classes.contextGroupBoundaries}`}
                          >
                            <div className={classes.contextGroupHeader}>{t(group.label)}</div>
                            <div className={classes.indicatorGrid}>
                              {CONTEXT_LAYER_FIELDS
                                .filter(descriptor => (
                                  descriptor.group === group.key &&
                                  (!descriptor.boolean || normalizeBoolean(selectedExplanationRow[descriptor.field]) !== null)
                                ))
                                .map(descriptor => (
                                  <div
                                    key={descriptor.field}
                                    className={classes.indicatorRow}
                                    data-testid={`context-layer-${descriptor.field}`}
                                  >
                                    <span className={classes.indicatorLabel}>{t(descriptor.label)}</span>
                                    {renderContextValue(selectedExplanationRow, descriptor)}
                                  </div>
                                ))}
                            </div>
                          </section>
                        ))}
                      </div>
                    </div>
                    </Card>
                  )}
                  <Card
                    className={classes.explanationBox}
                    data-testid="deforestation-explanation-box"
                    elevation={0}
                  >
                    <div className={classes.explanationBoxHeader}>
                      {t('AI explanation')}
                    </div>
                    <div className={classes.explanationBoxBody}>
                    {isExplanationLoading ? (
                      <div className={classes.loadingContainer}>
                        <CircularProgress size={34} color="primary" thickness={3} className={classes.loadingAnimation} />
                        <Typography variant="body1" className={classes.loadingAnimation} style={{ marginTop: 16, color: theme.palette.text.secondary }}>
                          Preparing AI analysis for parcel {expandedParcelId}...
                        </Typography>
                      </div>
                    ) : (
                      <ReactMarkdown
                        className={classes.markdownContent}
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                        components={{
                          code: ({node, inline, className, children, ...props}) => {
                            const value = String(children).trim();
                            if (inline && value.includes('Ind_')) {
                              return <span className={classes.indicatorCode}>{value}</span>;
                            }
                            if (inline && value === 'yes') {
                              return <span className={classes.indicatorValueYes}>YES</span>;
                            }
                            if (inline && value === 'no') {
                              return <span className={classes.indicatorValueNo}>NO</span>;
                            }
                            return !inline ? (
                              <pre style={{ overflowX: 'auto' }}>
                                <code className={className} {...props}>{children}</code>
                              </pre>
                            ) : (
                              <code className={className} {...props}>{children}</code>
                            );
                          },
                        }}
                      >
                        {explanationText}
                      </ReactMarkdown>
                    )}
                    </div>
                  </Card>
              </div>
            </div>
          ) : (
            <>
              <div className={classes.controlsContainer}>
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                  <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder={t(detectedPublicMode ? 'Search by ID, name or risk' : 'Search parcels by ID, name or risk')}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon className={classes.searchIcon} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>

                {filteredRows.length > 0 && (
                  <div className={classes.statsContainer}>
                    <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                      {t('Summary')}:
                    </Typography>
                    <Chip label={`${t('Low Risk')}: ${riskStats.low}/${riskStats.total}`} className={`${classes.statChip} ${classes.lowRiskChip}`} size="small" />
                    <Chip label={`${t('High Risk')}: ${riskStats.high}/${riskStats.total}`} className={`${classes.statChip} ${classes.highRiskChip}`} size="small" />
                    <Chip label={`${t('More Info')}: ${riskStats.info}/${riskStats.total}`} className={`${classes.statChip} ${classes.moreInfoChip}`} size="small" />
                  </div>
                )}

                <div className={classes.controlsRight}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={downloadExcel}
                    disabled={filteredRows.length === 0}
                    className={classes.downloadButton}
                    startIcon={<GetAppIcon />}
                  >
                    {t('Download Excel')}
                  </Button>
                </div>
              </div>

              <div className={classes.dataGridSide}>
                <TableContainer className={classes.summaryTableContainer} data-testid="deforestation-summary-table">
                  <Table className={classes.summaryTable} size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.expandCell} />
                        <TableCell>{t('Parcel')}</TableCell>
                        <TableCell>{t('Audit Date')}</TableCell>
                        <TableCell align="center">{t('Risk Acrop')}</TableCell>
                        {!detectedPublicMode && <TableCell align="center" className={classes.actionCell}>{t('Actions')}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={detectedPublicMode ? 4 : 5}>
                            <div className={classes.noDataMessage}>
                              <Typography variant="body1">{t('No parcels found')}</Typography>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                      {paginatedRows.map(row => {
                        const detailsOpen = expandedIndicatorsId === row.id;
                        const risk = riskPresentation(row.risk_acrop);
                        return (
                          <React.Fragment key={row.id}>
                            <TableRow
                              className={`${classes.summaryRow}${detailsOpen ? ` ${classes.summaryRowExpanded}` : ''}`}
                              onClick={() => setExpandedIndicatorsId(detailsOpen ? null : row.id)}
                            >
                              <TableCell className={classes.expandCell}>
                                <IconButton
                                  size="small"
                                  className={`${classes.chevronButton}${detailsOpen ? ` ${classes.chevronButtonOpen}` : ''}`}
                                  aria-label={`${detailsOpen ? 'Hide' : 'Show'} details for ${row.parcel_name}`}
                                >
                                  <ChevronRightIcon fontSize="small" />
                                </IconButton>
                              </TableCell>
                              <TableCell className={classes.summaryParcelCell}>
                                <span className={classes.parcelNameRow}>
                                  <Tooltip title={row.parcel_name || ''}>
                                    <span className={classes.parcelName}>{row.parcel_name}</span>
                                  </Tooltip>
                                  {hasTracesWarning(row) && (
                                    <span onClick={event => event.stopPropagation()}>
                                      <TracesWarning computed={row} t={t} fontSize={16} />
                                    </span>
                                  )}
                                </span>
                                <span className={classes.parcelId}>ID {row.parcel_id}</span>
                              </TableCell>
                              <TableCell className={classes.summaryDateCell}>{row.audit_date || '—'}</TableCell>
                              <TableCell align="center">
                                <span className={`${classes.riskBadge} ${risk.className}`}>{risk.label}</span>
                              </TableCell>
                              {!detectedPublicMode && (
                                <TableCell align="center">
                                  <Tooltip title={t('Show AI explanation')}>
                                    <IconButton
                                      ref={node => {
                                        if (
                                          node &&
                                          returnFocusParcelIdRef.current === row.parcel_id
                                        ) {
                                          returnFocusParcelIdRef.current = null;
                                          node.focus();
                                        }
                                      }}
                                      size="small"
                                      className={classes.actionButton}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleExplainClick(row.parcel_id);
                                      }}
                                      aria-label={`Show explanation for ${row.parcel_name}`}
                                    >
                                      <EmojiObjectsIcon className={classes.lightbulbIcon} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                            <TableRow className={classes.detailRow}>
                              <TableCell colSpan={detectedPublicMode ? 4 : 5}>
                                <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                                  <div className={classes.detailPanel}>
                                    <div className={classes.detailHeading}>{t('Risk indicators')}</div>
                                    <div className={classes.indicatorGrid}>
                                      {RISK_INDICATORS.map(indicator => (
                                        <div
                                          key={indicator.field}
                                          className={classes.indicatorRow}
                                          data-testid={`risk-indicator-${indicator.field}`}
                                        >
                                          <span className={classes.indicatorLabel}>
                                            <span className={classes.indicatorNumber}>I{indicator.number}</span>
                                            {t(indicator.label)}
                                          </span>
                                          {renderIndicatorChip(row[indicator.field], indicator)}
                                        </div>
                                      ))}
                                    </div>

                                    <div className={`${classes.detailHeading} ${classes.contextHeading}`}>
                                      {t('Context layers')}
                                    </div>
                                    <div className={classes.contextGroups}>
                                      {CONTEXT_LAYER_GROUPS.map(group => (
                                        <section
                                          key={group.key}
                                          className={`${classes.contextGroupCard} ${group.key === 'land' ? classes.contextGroupLand : classes.contextGroupBoundaries}`}
                                        >
                                          <div className={classes.contextGroupHeader}>{t(group.label)}</div>
                                          <div className={classes.indicatorGrid}>
                                            {CONTEXT_LAYER_FIELDS
                                              .filter(descriptor => (
                                                descriptor.group === group.key &&
                                                (!descriptor.boolean || normalizeBoolean(row[descriptor.field]) !== null)
                                              ))
                                              .map(descriptor => (
                                                <div
                                                  key={descriptor.field}
                                                  className={classes.indicatorRow}
                                                  data-testid={`context-layer-${descriptor.field}`}
                                                >
                                                  <span className={classes.indicatorLabel}>{t(descriptor.label)}</span>
                                                  {renderContextValue(row, descriptor)}
                                                </div>
                                              ))}
                                          </div>
                                        </section>
                                      ))}
                                    </div>
                                  </div>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  className={classes.summaryPagination}
                  component="div"
                  count={filteredRows.length}
                  page={tablePage}
                  onPageChange={(_, newPage) => setTablePage(newPage)}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={(event) => handlePageSizeChange(parseInt(event.target.value, 10))}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeforestationButton;
